/**
 * upgradeAcetateMaterials — port of Torga's acetate material upgrade technique.
 *
 * After GLB load, traverses the scene and upgrades acetate-identified
 * MeshStandardMaterial instances to MeshPhysicalMaterial with transmission,
 * IOR, clearcoat, and attenuation parameters. This gives acetate frames the
 * "wet plastic" depth that defines real-world acetate eyewear.
 *
 * Reference: `torga/web-try-on/src/utils/lib/visage/loading.js:806-1045` (separate repo)
 *
 * Parameters ported from Torga defaults:
 *   transmission:       0.78   — partial light transmission through the material
 *   thickness:          0.0018 — physical thickness in scene units (metres scale)
 *   ior:                1.49   — refractive index of acetate (≈ PMMA plastic)
 *   attenuationDistance: 0.03  — how quickly light attenuates inside the material
 *   clearcoat:          1.0    — full clearcoat gloss layer
 *   clearcoatRoughness: 0.06   — near-mirror clearcoat (slightly imperfect)
 *   roughness:          0.12   — base roughness for acetate (overrides GLB default)
 *   metalness:          0      — acetate is never metallic
 *
 * Detection heuristic (matches Torga logic):
 *   - material.name contains "acetate" (case-insensitive), OR
 *   - material.transparent === true AND material.metalness < 0.1
 *     (transparent overlay with no metalness = acetate-like layer)
 *
 * @module
 */

import type { Object3D, Mesh, Material } from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Subset of the Three.js namespace needed by upgradeAcetateMaterials.
 * Callers pass `await import('three')` — this module never imports Three.js
 * at module scope to stay tree-shakeable and SSR-safe.
 */
export interface AcetateThreeModule {
  Mesh: typeof import('three').Mesh;
  MeshPhysicalMaterial: typeof import('three').MeshPhysicalMaterial;
  Color?: typeof import('three').Color;
}

/**
 * Options for upgradeAcetateMaterials.
 * All physical material properties can be overridden; defaults match Torga.
 */
export interface AcetateUpgradeOptions {
  /**
   * Partial transmission through the material (0 = opaque, 1 = fully transmissive).
   * Default: 0.78 (Torga default — realistic acetate translucency).
   */
  transmission?: number;
  /**
   * Physical thickness in scene units (metres scale).
   * Default: 0.0018 (Torga default).
   */
  thickness?: number;
  /**
   * Index of refraction. Acetate ≈ PMMA ≈ 1.49.
   * Default: 1.49 (Torga default).
   */
  ior?: number;
  /**
   * Attenuation distance — how quickly light is absorbed inside the material.
   * Default: 0.03 (Torga default).
   */
  attenuationDistance?: number;
  /**
   * Clearcoat intensity (0 = no clearcoat, 1 = full gloss layer).
   * Default: 1.0 (Torga default).
   */
  clearcoat?: number;
  /**
   * Clearcoat roughness (0 = mirror, 1 = fully rough).
   * Default: 0.06 (Torga default — slightly imperfect gloss).
   */
  clearcoatRoughness?: number;
  /**
   * Whether to force roughness to 0.12 on acetate materials.
   * When false, the GLB-authored roughness is preserved (unless it's the
   * initial Three.js default of 1.0, which would look flat).
   * Default: true.
   */
  forceRoughness?: boolean;
}

// ---------------------------------------------------------------------------
// Detection heuristic
// ---------------------------------------------------------------------------

/**
 * Returns true when the material looks like an acetate frame layer.
 *
 * Matches Torga's two-path detection:
 *   1. Explicit name: material name contains "acetate" (case-insensitive)
 *   2. Transparent overlay: transparent=true AND metalness<0.1
 *      (coloured translucent plastic, not a lens or metal part)
 */
function isAcetateMaterial(mat: Material & { metalness?: number }): boolean {
  const name = mat.name?.toLowerCase() ?? '';
  if (name.includes('acetate')) return true;

  // Transparent + non-metallic = acetate-like overlay
  if (mat.transparent === true) {
    const metalness = mat.metalness ?? 0;
    if (metalness < 0.1) return true;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Upgrade acetate-identified MeshStandardMaterial instances in the scene tree
 * to MeshPhysicalMaterial with Torga-sourced transmission/IOR/clearcoat params.
 *
 * Safe to call on any scene — non-acetate meshes are left untouched. Already-
 * physical materials are also skipped (idempotent for re-load scenarios).
 *
 * @param THREE  - Three.js module (caller provides via dynamic import)
 * @param scene  - Root Object3D to traverse (typically gltf.scene)
 * @param opts   - Optional parameter overrides (defaults match Torga)
 * @returns      - Number of materials upgraded (0 if none matched)
 */
export function upgradeAcetateMaterials(
  THREE: AcetateThreeModule,
  scene: Object3D,
  opts: AcetateUpgradeOptions = {},
): number {
  // Resolve options with Torga defaults
  const transmission       = opts.transmission       ?? 0.78;
  const thickness          = opts.thickness          ?? 0.0018;
  const ior                = opts.ior                ?? 1.49;
  const attenuationDistance = opts.attenuationDistance ?? 0.03;
  const clearcoat          = opts.clearcoat          ?? 1.0;
  const clearcoatRoughness = opts.clearcoatRoughness ?? 0.06;
  const forceRoughness     = opts.forceRoughness     ?? true;

  let upgraded = 0;

  try {
    scene.traverse((obj: Object3D) => {
      // Only process Mesh instances
      if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
      const mesh = obj as Mesh;

      const mat = mesh.material as Material & {
        isMeshPhysicalMaterial?: boolean;
        isMeshStandardMaterial?: boolean;
        metalness?: number;
        roughness?: number;
      };

      if (!mat) return;

      // Skip already-physical — idempotent for variant-switch re-apply
      if (mat.isMeshPhysicalMaterial) return;

      // Only upgrade Standard materials (non-PBR sources are too rare in our GLBs)
      if (!mat.isMeshStandardMaterial) return;

      // Check acetate heuristic
      if (!isAcetateMaterial(mat as Material & { metalness?: number })) return;

      // Create and configure the upgraded physical material.
      // Use MeshStandardMaterial.prototype.copy (Torga technique) to avoid
      // MeshPhysicalMaterial.copy() expecting physical-only fields on source.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const PhysMat = THREE.MeshPhysicalMaterial as any;
      const upgraded_mat = new PhysMat() as Material & {
        isMeshPhysicalMaterial: boolean;
        transmission: number;
        thickness: number;
        ior: number;
        attenuationDistance: number;
        attenuationColor: { copy: (c: unknown) => void };
        clearcoat: number;
        clearcoatRoughness: number;
        roughness: number;
        metalness: number;
        transparent: boolean;
        depthWrite: boolean;
        opacity: number;
        defines?: Record<string, string>;
        name: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        copy: (src: any) => void;
        dispose: () => void;
      };

      // Copy standard PBR properties using MeshStandardMaterial.prototype.copy
      // (matches Torga's approach to avoid prototype field mismatches).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const StdProto = (Object.getPrototypeOf(Object.getPrototypeOf(upgraded_mat)) as any);
      if (typeof StdProto?.copy === 'function') {
        StdProto.copy.call(upgraded_mat, mat);
      } else {
        // Fallback: direct copy if prototype chain differs (Three.js version variance)
        upgraded_mat.copy(mat);
      }

      // Ensure the Physical shader path is active
      upgraded_mat.defines = { STANDARD: '', PHYSICAL: '' };
      upgraded_mat.name = mat.name;

      // Force metalness=0 and roughness=0.12 (Torga: acetate is non-metallic)
      upgraded_mat.metalness = 0;
      if (forceRoughness) {
        upgraded_mat.roughness = 0.12;
      }

      // Apply Torga's physical acetate properties
      upgraded_mat.transmission       = transmission;
      upgraded_mat.thickness          = thickness;
      upgraded_mat.ior                = ior;
      upgraded_mat.attenuationDistance = attenuationDistance;

      // Attenuation colour: match frame colour so tinted acetate looks correct.
      // Access via the source mat's color if available.
      const srcColor = (mat as unknown as { color?: { copy: (c: unknown) => void } }).color;
      if (srcColor && upgraded_mat.attenuationColor) {
        upgraded_mat.attenuationColor.copy(srcColor);
      }

      upgraded_mat.clearcoat          = clearcoat;
      upgraded_mat.clearcoatRoughness = clearcoatRoughness;

      // Keep transmission stable: opacity=1, transparent=true, depthWrite=true
      // (same as Torga: transmission workflow requires transparent=true but
      //  depthWrite=true keeps correct sort order with other frame parts)
      upgraded_mat.opacity     = 1;
      upgraded_mat.transparent = true;
      upgraded_mat.depthWrite  = true;

      // Swap material on mesh and dispose the old one
      mesh.material = upgraded_mat as unknown as Material;
      mat.dispose();

      upgraded++;
    });
  } catch {
    // Defensive: test mocks or rare Three.js version mismatches — skip silently.
    // Caller can check return value to detect if upgrade ran.
  }

  return upgraded;
}
